import React from 'react'
import { motion } from 'framer-motion'
import { Network, LayoutGrid, Users, ShieldCheck, Route } from 'lucide-react'
import { Section, Card } from './ui'
import { PILLARS } from '../constants'
import { cn } from '../utils'

const iconMap = {
  network: Network,
  grid: LayoutGrid,
  users: Users,
  shield: ShieldCheck,
  route: Route,
} as const

const PillarsSection: React.FC = () => {
  return (
    <Section id="pillars" background="default" padding="xl">
      <Section.Header
        subtitle="Strategic Pillars"
        title="Five ways we help Africa move from insight to impact."
        description="Each pillar is a discipline we bring to partnerships — from data and AI to policy, talent, and delivery."
        align="center"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {PILLARS.map((pillar, index) => {
          const Icon = iconMap[pillar.icon as keyof typeof iconMap] ?? Network
          const isBlue = pillar.accent === 'blue'
          return (
            <motion.div
              key={pillar.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{
                duration: 0.5,
                delay: index * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
              className={cn(
                // Make the fifth (odd-count) pillar span two on lg to keep grid balanced
                PILLARS.length === 5 && index === 3 && 'lg:col-start-2',
                PILLARS.length === 5 && index === 4 && 'lg:col-start-3'
              )}
            >
              <Card variant="default" padding="lg" hover className="h-full group">
                <div
                  className={cn(
                    'w-11 h-11 rounded-lg flex items-center justify-center mb-5',
                    'transition-colors duration-200',
                    isBlue
                      ? 'bg-secondary-50 dark:bg-secondary-900/40 text-secondary-500 dark:text-secondary-300 group-hover:bg-secondary-100'
                      : 'bg-primary-50 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 group-hover:bg-primary-100'
                  )}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 leading-snug">
                  {pillar.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {pillar.description}
                </p>
              </Card>
            </motion.div>
          )
        })}
      </div>
    </Section>
  )
}

export default PillarsSection
